// Refonte du suivi, lot 5 : contrôler EXACTEMENT ce que la cliente recevra.
//
// Une seule transformation, la même que la base (V97.275
// suivi_contenu_tel_que_publie) : pour un changement de phase, la phase cible
// devient active et l'ancienne phase active passe « completed ». Le contrôle
// clinique, l'aperçu, l'empreinte envoyée à la validation et le livrable app
// partent tous de cette ligne « telle que publiée ».
//
// Contrôles (les existants, rien de nouveau) :
//   * clairance clinique du plan et de tout le texte du livrable app
//     (assertPlanClinicallyCleared) ;
//   * aliments exclus dans menus, recettes, rotation, Meal Prep, frigo et
//     textes rédigés (findPublicationFoodBlockers) ;
//   * programme MGD : aucun override, et les confirmations déjà en place
//     (repas vérifiés au regard des exclusions en prose, relecture des textes)
//     sont exigées.
// Chaque alerte montre la phrase et l'aliment (demande d'Anissa).

import { canonicalJsonbText } from '../../services/canonicalJsonb';
import { buildPlanRenderInputsV1 } from '../../services/planDeliveryContract';
import { assertPlanClinicallyCleared } from '../../services/clinicalClearance';
import { aiMentionMessage, findAiMentionsDeepDetailed, identityNamesOf } from '../../services/aiMentionGuard';
import { normalizeForMatch } from '../../services/clinicalInteractions';
import {
  buildClientAppPublicationPayload,
  collectPublicationText,
} from '../../services/publishToClientApp';
import {
  findPublicationFoodBlockers,
  narrativeProseReviewState,
} from '../../services/weekMenus';

export const DECISION_OVERRIDE = 'anissa_clinical_override';

async function sha256Texte(texte) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Empreinte indisponible dans ce navigateur.');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(texte));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function estObjet(valeur) {
  return valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur);
}

/** Miroir exact de public.suivi_contenu_tel_que_publie (V97.275). */
export function ligneTelleQuePubliee(ligne) {
  if (!ligne || ligne.suivi_motif !== 'changement_phase') return ligne;
  const cible = ligne.followup_data?.suivi_phase_cible?.to
    ?? ligne.followup_data?.phaseTransitionDraft?.toPhaseId
    ?? null;
  const phases = ligne.protocol_phases?.phases;
  if (cible == null || ligne.active_phase_id === cible || !Array.isArray(phases)) return ligne;
  return {
    ...ligne,
    protocol_phases: {
      ...ligne.protocol_phases,
      phases: phases.map((phase) => {
        if (phase?.id === cible) return { ...phase, status: 'active' };
        if (phase?.status === 'active') return { ...phase, status: 'completed' };
        return phase;
      }),
    },
    active_phase_id: cible,
  };
}

/** Objet haché par public.suivi_empreinte_contenu (V97.251). */
export function objetEmpreinte(ligne) {
  let editorial = ligne?.editorial_overrides ?? null;
  if (estObjet(editorial)) {
    editorial = { ...editorial };
    delete editorial.week_menus_review;
    delete editorial.narrative_prose_review;
  }
  return {
    version: 1,
    nutrition_plan: ligne?.nutrition_plan ?? null,
    supplements: ligne?.supplements ?? null,
    recipes: ligne?.recipes ?? null,
    meal_recipes: ligne?.meal_recipes ?? null,
    editorial_overrides: editorial,
    fiche_frigo_json: ligne?.fiche_frigo_json ?? null,
    intro_letter: ligne?.intro_letter ?? null,
    label: ligne?.label ?? null,
    protocol_phases: ligne?.protocol_phases ?? null,
    active_phase_id: ligne?.active_phase_id ?? null,
  };
}

/** Même valeur que suivi_empreinte_contenu(ligne) en base. */
export async function empreinteContenu(ligne) {
  return sha256Texte(canonicalJsonbText(objetEmpreinte(ligne)));
}

/**
 * Livrable app d'une version du nouveau suivi : le payload habituel, construit
 * sur la ligne telle que publiée. Une version du suivi s'applique dès sa
 * publication : jamais de jalon S4/S8 différé (followup_week = 0).
 */
export function construireLivrableApp(client, lignePubliee) {
  return { ...buildClientAppPublicationPayload(client, lignePubliee), followup_week: 0 };
}

/**
 * Empreinte des entrées du rendu (fiche + version telle que publiée), même
 * objet que suivi_empreinte_rendu (V97.287) : la base l'exige égale à la
 * validation (V97.290).
 */
export async function empreinteRendu(client, lignePubliee) {
  return sha256Texte(canonicalJsonbText(buildPlanRenderInputsV1(client, lignePubliee)));
}

/** Empreinte du livrable app, celle que vérifie l'API publish-plan. */
export async function empreinteLivrable(payload) {
  return sha256Texte(JSON.stringify(payload));
}

function textesDuLivrable(payload) {
  return collectPublicationText({
    title: payload?.title,
    objective: payload?.objective,
    nutrition_plan: payload?.nutrition_plan,
    sections: payload?.sections,
    journey_phases: payload?.journey_phases,
  });
}

/** Première phrase (ou ligne) du texte qui contient le terme, ou null. */
export function phraseContenant(texte, terme) {
  const cherche = normalizeForMatch(terme);
  if (!cherche || typeof texte !== 'string') return null;
  const morceaux = texte.split(/\n+|(?<=[.!?])\s+/);
  const trouve = morceaux.find((m) => normalizeForMatch(m).includes(cherche));
  return trouve ? trouve.trim().slice(0, 300) : null;
}

function termeEntreGuillemets(label) {
  const m = /«\s*([^»]+?)\s*»/.exec(String(label || ''));
  return m ? m[1] : null;
}

function alerteClinique(item, gravite, texte) {
  const aliment = termeEntreGuillemets(item?.label);
  return {
    gravite,
    type: item?.type || 'clinique',
    texte: item?.label || 'Alerte clinique.',
    aliment,
    phrase: item?.snippet || (aliment ? phraseContenant(texte, aliment) : null),
  };
}

// Messages de findPublicationFoodBlockers :
//   « ligne » mentionne un aliment exclu (aliment) — …   (menus, recettes…)
//   Section mentionne un aliment exclu (aliment) — …       (textes rédigés)
//   autres : confirmations à faire (exclusions en prose, relecture).
function alerteAliment(message, texte) {
  const ligne = /«\s*([^»]+?)\s*»\s*mentionne un aliment exclu \(([^)]+)\)/.exec(message);
  if (ligne) {
    return { gravite: 'bloquant', type: 'aliment_exclu', texte: message, phrase: ligne[1], aliment: ligne[2], procedure: false };
  }
  const section = /mentionne un aliment exclu \(([^)]+)\)/.exec(message);
  if (section) {
    return { gravite: 'bloquant', type: 'aliment_exclu', texte: message, phrase: phraseContenant(texte, section[1]), aliment: section[1], procedure: false };
  }
  return { gravite: 'bloquant', type: 'confirmation_requise', texte: message, phrase: null, aliment: null, procedure: true };
}

function sansDoublon(liste) {
  const vus = new Set();
  return liste.filter((item) => {
    const cle = `${item.type}|${item.label}`;
    if (vus.has(cle)) return false;
    vus.add(cle);
    return true;
  });
}

/**
 * Contrôle complet d'une version avant « Valider et publier ».
 *
 * @param {object} p
 * @param {object} p.client  fiche cliente (form, prénom, email…)
 * @param {object} p.ligne   ligne nutrition_consultations du brouillon
 * @param {boolean} p.mgd    programme MGD actif (hors dossier externe)
 * @returns {Promise<object>} lignePubliee, payload, contentSha256,
 *   appArtifactSha256, verdict (format suivi_valider_version), alertes,
 *   bloquant, confirmable (override possible), narratif (relecture MGD).
 */
export async function controlerVersion({ client, ligne, mgd = false }) {
  const lignePubliee = ligneTelleQuePubliee(ligne);
  const contentSha256 = await empreinteContenu(lignePubliee);
  let payload = null;
  let erreurLivrable = null;
  try {
    payload = construireLivrableApp(client, lignePubliee);
  } catch (error) {
    erreurLivrable = error?.message || 'Le contenu de l’app ne peut pas être construit.';
  }
  const texte = payload ? textesDuLivrable(payload) : String(lignePubliee?.nutrition_plan || '');
  const form = client?.form || {};

  const violations = [];
  const warnings = [];
  const alertes = [];

  if (!mgd) {
    // Clairance locale : plan seul, puis tout le texte que l'app affichera.
    for (const source of [String(lignePubliee?.nutrition_plan || ''), texte]) {
      const verdict = assertPlanClinicallyCleared(source, { form });
      violations.push(...(verdict.violations || []));
      warnings.push(...(verdict.warnings || []));
    }
  }

  // Toutes les clientes : jamais de mention d'outil ou d'IA dans l'app.
  // Mention sans ambiguïté (chatgpt, openai, gpt-4…) : bloquante ; mention
  // possible (« IA », préambule…) : signalée à Anissa, qui décide.
  const mentionsOutil = findAiMentionsDeepDetailed(payload
    ? { title: payload.title, objective: payload.objective, nutrition_plan: payload.nutrition_plan, sections: payload.sections, journey_phases: payload.journey_phases }
    : String(lignePubliee?.nutrition_plan || ''), { identityNames: identityNamesOf(client) });
  if (mentionsOutil.certain.length > 0) {
    violations.push({ type: 'mention_outil', severity: 'high', label: aiMentionMessage(mentionsOutil.certain) });
  }
  if (mentionsOutil.possible.length > 0) {
    warnings.push({ type: 'mention_outil_possible', severity: 'low', label: aiMentionMessage(mentionsOutil.possible) });
  }

  const blocages = payload
    ? findPublicationFoodBlockers({ client, consultation: lignePubliee, payload })
    : [];
  const alertesAliments = blocages.map((message) => alerteAliment(message, texte))
    // Hors MGD, seuls les aliments exclus réellement présents comptent (même
    // règle que l'ancien circuit, qui ne demandait ces confirmations qu'au MGD).
    .filter((alerte) => mgd || !alerte.procedure);

  const violationsUniques = sansDoublon(violations);
  const warningsUniques = sansDoublon(warnings);
  for (const item of violationsUniques) alertes.push(alerteClinique(item, 'bloquant', texte));
  for (const item of warningsUniques) alertes.push(alerteClinique(item, 'attention', texte));
  alertes.push(...alertesAliments);

  const violationsVerdict = [
    ...violationsUniques.map(({ type, severity, label }) => ({ type, severity, label })),
    ...alertesAliments.map((alerte) => ({
      type: alerte.type,
      severity: 'high',
      label: alerte.texte,
    })),
  ];
  const verdict = {
    cleared: violationsVerdict.length === 0,
    content_sha256: contentSha256,
    severity: violationsVerdict.length === 0 ? 'none' : 'high',
    violations: violationsVerdict,
    warnings: warningsUniques.map(({ type, severity, label }) => ({ type, severity, label })),
  };

  const narratif = mgd && payload
    ? narrativeProseReviewState({ client, consultation: lignePubliee, payload })
    : null;

  return {
    lignePubliee,
    payload,
    erreurLivrable,
    contentSha256,
    renderInputsSha256: await empreinteRendu(client, lignePubliee),
    appArtifactSha256: payload ? await empreinteLivrable(payload) : null,
    verdict,
    alertes,
    bloquant: !verdict.cleared || Boolean(erreurLivrable),
    // Override conscient : jamais pour MGD, jamais pour une confirmation à
    // faire, jamais si le livrable lui-même n'a pas pu être construit.
    confirmable: !mgd && !verdict.cleared && !erreurLivrable
      && alertesAliments.every((alerte) => !alerte.procedure),
    narratif,
    mgd,
  };
}

/** Décision scellée (format payload_override_clinique_plan_valide). */
export function construireOverride(verdict) {
  const sansEmpreinte = { ...(verdict || {}) };
  delete sansEmpreinte.content_sha256;
  return {
    version: 1,
    decision: DECISION_OVERRIDE,
    confirmed: true,
    verdict: sansEmpreinte,
    violation_types: [...new Set((sansEmpreinte.violations || []).map((v) => v.type))].sort(),
  };
}
